import { ChangeEvent, PropsWithChildren, createContext, useContext, useState } from 'react';
import toast from 'react-hot-toast';

import styled from '@emotion/styled';
import { getServiceName } from '@repo/utils';
import * as yup from 'yup';

import useBooleanState from 'hooks/useBooleanState';
import useCheckMediaQuery from 'hooks/useCheckMediaQuery';
import useValidation from 'hooks/useValidation';
import { WithdrawalsInfoResponseDto, useGetWithdrawalsInfo } from 'services/hooks/withdrawal';
import { useModalStackStore } from 'store/modalStackStore';
import { convertStringToNumber, formatLocaleNumber, inputOnlyNumberTypeString } from 'utils/number';
import { translation } from 'utils/translation';

import Flex from 'components/util/Flex';

import { ChangeBubbleData } from './AvailableChangeBubbleModal';
import { WithdrawalData } from './AvailableWithdrawalModal';
import { SInformationItem } from '../shared/style';

/**
 * Modal Context (모달 내의 컴포넌트 사이에서 사용될 state, function)
 */
interface WithdrawalContextType {
  // 서버 데이터
  withdrawalData?: WithdrawalsInfoResponseDto;
  isWithdrawalDataLoading: boolean;
  // 입력 영역
  withdrawalAmountInput: `${number}` | '';
  // handleWithdrawalAmountInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  changeWithdrawalAmountInput: (amount: `${number}` | '') => void;
  errors: Partial<Record<string | number | symbol, string>>;
  validate: (
    values: { withdrawalAmount: number },
    validateOptions?: ChangeBubbleData & WithdrawalData
  ) => Promise<boolean>;
  resetErrors: () => void;
  goodsUnit: number;
  isWithdrawalEnabled: boolean;
}

const WithdrawalContext = createContext<WithdrawalContextType | null>(null);
const useWithdrawalContext = () => {
  const context = useContext(WithdrawalContext);
  if (!context) throw new Error('useWithdrawalContext must be used within a WithdrawalModal.Provider');
  return context;
};

/**
 * Modal Root (Modal Context을 감쌀 컨테이너(Provider))
 * 데이터를 받아서 하위 컴포넌트에 내려주는 역할을 한다.
 */
type WithdrawalModalRootProps = {
  withdrawalSchema: (
    validateOptions: ChangeBubbleData & WithdrawalData
  ) => yup.ObjectSchema<{ withdrawalAmount: number }>;
  goodsUnit: number; // 재화 단위 (현재 버블은 1000, 현금은 10000원 단위)
  type: 'bubble' | 'cash';
};

const AvailableModalRoot = ({
  children,
  withdrawalSchema,
  goodsUnit,
  type,
}: PropsWithChildren<WithdrawalModalRootProps>) => {
  // Prefetching 고려됨

  const { data: withdrawalData, isFetching: isWithdrawalDataLoading } = useGetWithdrawalsInfo(type);
  const { validate, errors, resetErrors } = useValidation(withdrawalSchema);

  const [withdrawalAmountInput, setWithdrawalAmountInput] = useState<`${number}` | ''>('');
  const changeWithdrawalAmountInput = (amount: `${number}` | '') => {
    setWithdrawalAmountInput(amount);
  };

  const convertedWithdrawalAmount = convertStringToNumber(withdrawalAmountInput);

  const isWithdrawalEnabled = Boolean(
    !isWithdrawalDataLoading &&
    withdrawalData &&
    convertedWithdrawalAmount >= withdrawalData.minAmount &&
    convertedWithdrawalAmount <= withdrawalData.availableAfterTaxWithdrawal &&
    convertedWithdrawalAmount % goodsUnit === 0 &&
    !errors.withdrawalAmount
  );

  return (
    <WithdrawalContext.Provider
      value={{
        withdrawalAmountInput,
        changeWithdrawalAmountInput,
        isWithdrawalDataLoading,
        withdrawalData,
        validate,
        errors,
        resetErrors,
        goodsUnit,
        isWithdrawalEnabled,
      }}
    >
      <SAvailableWithdrawalModal>{children}</SAvailableWithdrawalModal>
    </WithdrawalContext.Provider>
  );
};

/**
 * Modal Header
 */
type AvailableModalHeaderProps = { title: string };
const AvailableModalHeader = ({ title }: AvailableModalHeaderProps) => {
  return (
    <SAvailableModalHeader>
      <h2 className="title">{title}</h2>
    </SAvailableModalHeader>
  );
};

/**
 * Modal Body
 */
type AvailableModalBodyProps = {
  availableGoodsText: string; // 전환 가능
  requestedGoodsText: string; // 신청
  units: [string, string]; // 원, 버블 (단수,복수)
  GuideLineListComponent: React.ReactNode;
};
const AvailableModalBody = ({
  availableGoodsText,
  requestedGoodsText,
  units,
  GuideLineListComponent,
}: AvailableModalBodyProps) => {
  const { isMobile, isXSmall } = useCheckMediaQuery();
  const {
    withdrawalData,
    withdrawalAmountInput,
    changeWithdrawalAmountInput,
    errors,
    resetErrors,
    goodsUnit,
    validate,
    isWithdrawalEnabled,
  } = useWithdrawalContext();
  const { value: isInputFocused, setTrue: handleInputFocus, setFalse: handleFocusBlur } = useBooleanState(false);

  const handleWithdrawalAmountInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!withdrawalData) return;
    resetErrors();

    const withdrawalAmountString = inputOnlyNumberTypeString(event.target.value);
    const withdrawalAmountNumber = convertStringToNumber(withdrawalAmountString);

    const changedWithdrawalAmount = Math.min(withdrawalData.availableAfterTaxWithdrawal, withdrawalAmountNumber);

    changeWithdrawalAmountInput(`${changedWithdrawalAmount}`);

    if (changedWithdrawalAmount) {
      validate(
        { withdrawalAmount: Number(changedWithdrawalAmount) },
        {
          minAmount: withdrawalData.minAmount,
          maxAmount: withdrawalData.maxAmount,
          goodsUnit,
        }
      );
    } else {
      resetErrors();
    }
  };

  const convertibleUnit =
    withdrawalData?.availableAfterTaxWithdrawal && Number(withdrawalData.availableAfterTaxWithdrawal) > 1
      ? units[1]
      : units[0];

  const inputUnit = Number(withdrawalAmountInput) <= 1 ? units[0] : units[1];

  return (
    <SAvailableWithdrawalModalBody>
      <Flex direction="column">
        <Flex direction="column" gap={isXSmall || isMobile ? 0 : 6}>
          {/* 출금 가능 금액 + 입력 */}
          <Flex direction="column" gap={8}>
            {/* 출금 가능 금액 정보 */}
            <SInformationItemWrapper>
              <span>
                {availableGoodsText} <strong className="emphasize">*</strong>
              </span>
              <span className="bold">
                {withdrawalData?.availableAfterTaxWithdrawalFormat} {convertibleUnit}
              </span>
            </SInformationItemWrapper>
            {/* 입력 */}
            <SInputContainerWrapper $invisibleBorderBottom $isMobile={isMobile}>
              <span>{requestedGoodsText}</span>
              <SInputContainer direction="column">
                <SNumberInputContainer
                  justify="space-between"
                  align="center"
                  $isFocused={isInputFocused}
                  $isError={!isWithdrawalEnabled}
                >
                  <SNumberInput
                    type="text"
                    value={formatLocaleNumber(withdrawalAmountInput)}
                    onChange={handleWithdrawalAmountInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleFocusBlur}
                  />
                  <SUnit>{withdrawalAmountInput !== '' && inputUnit}</SUnit>
                </SNumberInputContainer>
                <SError>{errors.withdrawalAmount}</SError>
              </SInputContainer>
            </SInputContainerWrapper>
          </Flex>
          {/* 가이드라인 */}
          <SGuideInformation>{GuideLineListComponent}</SGuideInformation>
        </Flex>
      </Flex>
    </SAvailableWithdrawalModalBody>
  );
};

/**
 * Modal Footer
 */
type AvailableModalFooterProps = {
  postWithdrawalRequest: (afterTaxAmount: number) => Promise<void>;
  isProcessWithdrawal: boolean;
  withdrawalSuccessToastText: string;
  withdrawalFailureToastText: string;
};
const AvailableModalFooter = ({
  postWithdrawalRequest,
  isProcessWithdrawal,
  withdrawalFailureToastText,
  withdrawalSuccessToastText,
}: AvailableModalFooterProps) => {
  const { isWithdrawalDataLoading, withdrawalAmountInput, withdrawalData, validate, goodsUnit, isWithdrawalEnabled } =
    useWithdrawalContext();
  const { closeModal } = useModalStackStore();

  const handleRequestWithdrawalSubmit = async () => {
    if (!withdrawalData) return null;
    if (isProcessWithdrawal) return null;

    try {
      const isValidationSuccess = await validate(
        { withdrawalAmount: Number(withdrawalAmountInput) },
        {
          minAmount: withdrawalData.minAmount,
          maxAmount: withdrawalData.maxAmount,
          goodsUnit,
        }
      );
      if (isValidationSuccess) {
        try {
          await postWithdrawalRequest(Number(withdrawalAmountInput));
          toast.success(withdrawalSuccessToastText, {
            icon: null,
            position: 'top-center',
            style: {
              backgroundColor: '#393939',
              color: '#ffffff',
              borderRadius: '8px',
              background: '#606D73',
              boxShadow: '0px 2px 4px 0px rgba(96, 109, 115, 0.30)',
            },
          });
        } catch (networkError) {
          console.error('Network Error:', networkError);
          toast.error(withdrawalFailureToastText, {
            position: 'top-center',
          });
        } finally {
          closeModal();
        }
      }
    } catch (yupValidationError) {
      if (yupValidationError instanceof yup.ValidationError) {
        console.error('Validation error:', yupValidationError.errors);
      } else {
        console.error('Network Error:', yupValidationError);
        closeModal();
        toast.error(withdrawalFailureToastText, {
          position: 'top-center',
        });
      }
    }
  };

  return (
    <Flex direction="row" justify="center" columnGap={10}>
      <SCancelButton onClick={closeModal}>{translation('chat.rewards_withdrawal_request_cancel')}</SCancelButton>
      <SRequestButton
        onClick={handleRequestWithdrawalSubmit}
        disabled={isWithdrawalDataLoading}
        $enabled={isWithdrawalEnabled}
        $isTingle={getServiceName() === 'tingle'}
      >
        {translation('chat.rewards_withdrawal_request_submit')}
      </SRequestButton>
    </Flex>
  );
};

const AvailableModal = Object.assign(AvailableModalRoot, {
  Header: AvailableModalHeader,
  Body: AvailableModalBody,
  Footer: AvailableModalFooter,
});

export default AvailableModal;

// Styled Component
const SAvailableWithdrawalModal = styled.div`
  width: 560px;
  padding: 20px 24px 30px;

  @media ${({ theme }) => theme.device.medium} {
    width: 100%;
    padding: 24px 24px;
  }
`;

const SAvailableModalHeader = styled(Flex)`
  padding: 16px;

  .title {
    ${({ theme }) => theme.font.head3_bw};
    text-align: center;
    width: 100%;
  }
`;
const SInformationItemWrapper = styled(SInformationItem)`
  padding: 16px 0;

  .emphasize {
    margin-left: 6px;
    color: ${({ theme }) => theme.color.static.warning};
  }
`;

const SAvailableWithdrawalModalBody = styled.div`
  padding: 0 24px;
  margin-bottom: 8px;

  @media ${({ theme }) => theme.device.medium} {
    padding: 0 0;
  }
`;

type SInputContainerWrapperProps = {
  $isMobile: boolean;
};
const SInputContainerWrapper = styled(SInformationItem)<SInputContainerWrapperProps>`
  padding: 16px 0 0;

  ${({ $isMobile }) =>
    $isMobile
      ? `
      flex-direction: row;
      `
      : `
      flex-direction: column;
      align-items: flex-start;
      row-gap: 4px;
      `}
`;
const SInputContainer = styled(Flex)`
  @media ${({ theme }) => theme.device.small} {
    width: 100%;
  }
`;

type SNumberInputContainerProps = {
  $isFocused: boolean;
  $isError: boolean;
};
const SNumberInputContainer = styled(Flex)<SNumberInputContainerProps>`
  position: relative;
  width: 260px;
  padding: 0 12px 0 12px;
  background: ${({ theme }) => theme.color.field.input};
  border: 1px solid
    ${({ theme, $isFocused, $isError }) =>
      $isFocused ? ($isError ? theme.color.static.warning : theme.color.static.primary) : 'transparent'};
  border-radius: 8px;
  color: ${({ theme }) => theme.color.text.TXT_3};
  ${({ theme }) => theme.font.body2_sb};

  @media ${({ theme }) => theme.device.medium} {
    width: 200px;
  }

  @media ${({ theme }) => theme.device.small} {
    width: 100%;
  }
`;

const SNumberInput = styled.input`
  height: 48px;
  text-align: right;
  flex: 1;
  color: ${({ theme }) => theme.color.text.TXT_3};
  ${({ theme }) => theme.font.body2_sb};

  @media ${({ theme }) => theme.device.medium} {
    min-width: 146px;
  }

  &:focus {
    outline: none;
  }
`;

const SUnit = styled.span`
  margin-left: 4px;
  min-width: 12px;
`;

const SError = styled.div`
  color: ${({ theme }) => theme.color.static.warning};
  height: 16px;
  text-align: right;
  padding: 4px 0;
  ${({ theme }) => theme.font.caption1_sb};
`;

const SGuideInformation = styled(Flex)`
  padding: 14px 15px;

  color: ${({ theme }) => theme.color.text.TXT_1};

  li {
    list-style-type: disc;
    font-size: 10px;
  }

  .text {
    ${({ theme }) => theme.font.caption1_r};
    word-break: normal;
  }

  .emphasize {
    color: ${({ theme }) => theme.color.static.warning};
  }

  @media ${({ theme }) => theme.device.small} {
    padding: 14px 15px 14px;
  }
`;

const SCancelButton = styled.button`
  width: 150px;
  padding: 12px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.line.line_2};
  background-color: transparent;
  color: ${({ theme }) => theme.color.text.TXT_1};
  ${({ theme }) => theme.font.body1_r};
`;

type SRequestButtonProps = {
  $enabled: boolean;
  // 팅글 리워드 CTA: yellow/black (임시, 디자인 제안 Figma 1072:58012). 버블챗은 기존 primary/white 유지
  $isTingle?: boolean;
};

const SRequestButton = styled.button<SRequestButtonProps>`
  padding: 12px 0;
  width: 180px;
  border-radius: 8px;
  background-color: ${({ theme, $enabled, $isTingle }) =>
    $enabled ? ($isTingle ? theme.color.brand.primary : theme.color.static.primary) : theme.color.buttons.disable};
  color: ${({ theme, $isTingle }) => ($isTingle ? '#000000' : theme.color.static.white)};
  ${({ theme }) => theme.font.body1_m};
`;
